import React from 'react';
import { Alert, AlertTitle, Box, Chip, Stack, Typography } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { REPS_COLORS } from '../utils/constants';

const QUADRANTS = ['reliability', 'efficiency', 'productivity', 'safety'];

/**
 * Determines alert severity based on divergence magnitude.
 * @param {number} divergence - Absolute divergence value
 * @returns {'info'|'warning'|'error'}
 */
function getSeverity(divergence) {
  if (divergence >= 4.0) return 'error';
  if (divergence >= 3.0) return 'warning';
  return 'info';
}

/**
 * Capitalizes the first letter of a string.
 */
function capitalize(str) {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
}

/**
 * DivergenceWarning Component
 *
 * Warns when survey scores diverge from document-derived scores by more than
 * a threshold (default 2.0) for any REPS quadrant.
 *
 * @param {object} props
 * @param {object} props.surveyScores - OE scores from surveys { reliability, efficiency, productivity, safety }
 * @param {object} props.documentScores - OE scores derived from document analysis (enrichedScores from useOEAdjustments)
 * @param {number} [props.threshold=2.0] - Divergence threshold to trigger warning
 */
function DivergenceWarning({ surveyScores, documentScores, threshold = 2.0 }) {
  // Don't render if no document scores exist
  if (!documentScores) return null;
  if (!surveyScores) return null;

  // Calculate divergence per REPS quadrant
  const divergences = QUADRANTS.map((quadrant) => {
    const surveyVal = typeof surveyScores[quadrant] === 'number' ? surveyScores[quadrant] : null;
    const docVal = typeof documentScores[quadrant] === 'number' ? documentScores[quadrant] : null;

    if (surveyVal === null || docVal === null) return null;

    const divergence = Math.abs(surveyVal - docVal);
    return {
      quadrant,
      surveyScore: surveyVal,
      documentScore: docVal,
      divergence,
      exceeds: divergence > threshold,
    };
  }).filter((d) => d !== null && d.exceeds);

  // Nothing to show if no quadrants exceed threshold
  if (divergences.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      {divergences.map(({ quadrant, surveyScore, documentScore, divergence }) => {
        const severity = getSeverity(divergence);
        const color = REPS_COLORS[quadrant] || '#ed6c02';

        return (
          <Alert
            key={quadrant}
            severity={severity}
            icon={<CompareArrowsIcon />}
            sx={{
              mb: 1,
              borderLeft: `4px solid ${color}`,
              '& .MuiAlert-icon': { color },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600 }}>
              Score Divergence: {capitalize(quadrant)}
            </AlertTitle>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2">
                Survey perceives {capitalize(quadrant)} at {surveyScore.toFixed(1)}, documents suggest {documentScore.toFixed(1)} — a divergence of {divergence.toFixed(1)} points.
              </Typography>
              <Chip
                label={`Survey: ${surveyScore.toFixed(1)}`}
                size="small"
                sx={{
                  bgcolor: color,
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
              <Chip
                label={`Documents: ${documentScore.toFixed(1)}`}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: color,
                  color,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
            </Stack>
          </Alert>
        );
      })}
    </Box>
  );
}

export default DivergenceWarning;
